// Types pour la détection des conflits entre absences et affectations
// Utilisés par: ModalAffectationChantier, ModalDateFinStatut, useAlertes

/**
 * Conflit détecté : une absence chevauche une période d'affectation potentielle
 * Retourné par la fonction SQL check_membre_absences()
 */
export interface AbsenceConflict {
  absence_id: string;
  type_absence: string;  // 'conges', 'maladie', 'formation', 'autre'
  date_debut: string;    // Format YYYY-MM-DD
  date_fin: string;      // Format YYYY-MM-DD
  jours_conflit: number; // Nombre de jours en chevauchement
  motif: string | null;
}

/**
 * Conflit détecté : une affectation existe pendant une période d'absence potentielle
 * Retourné par la fonction SQL check_membre_affectations()
 */
export interface AffectationConflict {
  affectation_id: string;
  chantier_id: string;
  chantier_nom: string;
  chantier_reference: string | null;
  date_debut: string;     // Format YYYY-MM-DD
  date_fin: string;       // Format YYYY-MM-DD
  jours_conflit: number;  // Nombre de jours en chevauchement
  heures_par_jour: number | null;
}

/**
 * Conflit complet entre une absence et une affectation existantes
 * Retourné par la fonction SQL get_all_conflits_absence_affectation()
 * Utilisé pour les alertes dans le Centre de Pilotage
 */
export interface ConflitAbsenceAffectation {
  // Informations membre
  membre_id: string;
  membre_nom: string;
  membre_prenom: string;
  membre_poste: string | null;

  // Informations absence
  absence_id: string;
  type_absence: string;
  absence_debut: string;
  absence_fin: string;
  absence_motif: string | null;

  // Informations affectation
  affectation_id: string;
  chantier_id: string;
  chantier_nom: string;
  chantier_reference: string | null;
  affectation_debut: string;
  affectation_fin: string;

  // Calcul
  jours_conflit: number;
}

/**
 * Résultat de la vérification des absences
 */
export interface CheckAbsencesResult {
  hasConflict: boolean;
  absences: AbsenceConflict[];
  totalJoursConflit: number;
}

/**
 * Résultat de la vérification des affectations
 */
export interface CheckAffectationsResult {
  hasConflict: boolean;
  affectations: AffectationConflict[];
  totalJoursConflit: number;
}

/**
 * Labels pour les types d'absence
 */
export const TYPE_ABSENCE_LABELS: Record<string, string> = {
  conges: 'Congés',
  maladie: 'Arrêt maladie',
  formation: 'Formation',
  autre: 'Autre absence',
};

/**
 * Obtenir le label d'un type d'absence
 */
export const getTypeAbsenceLabel = (type: string): string => {
  return TYPE_ABSENCE_LABELS[type] || type;
};

// ============================================================
// TYPES BULK - Pour l'affectation en masse (#2, #6, #12)
// ============================================================

/**
 * Conflit d'absence pour un membre spécifique (version bulk)
 * Retourné par la fonction SQL check_membres_absences_bulk()
 */
export interface BulkAbsenceConflict extends AbsenceConflict {
  membre_id: string;
}

/**
 * Conflit d'affectation pour un membre spécifique (version bulk)
 * Retourné par la fonction SQL check_membres_affectations_bulk()
 */
export interface BulkAffectationConflict extends AffectationConflict {
  membre_id: string;
}

/**
 * Disponibilité d'un membre pour une période (version bulk)
 * Retourné par la fonction SQL get_membres_disponibilite_bulk()
 */
export interface MembreDisponibilite {
  membre_id: string;
  a_absence: boolean;           // Le membre a une absence sur la période
  a_affectation: boolean;       // Le membre a déjà une affectation sur la période
  total_heures_jour: number;    // Total des heures déjà affectées par jour
  est_disponible: boolean;      // Synthèse : disponible si pas d'absence ET heures < 16
}

/**
 * Résultat de la vérification bulk des absences
 */
export interface BulkAbsenceCheckResult {
  conflits: BulkAbsenceConflict[];
  membres_avec_conflits: string[];
  membres_sans_conflits: string[];
}

/**
 * Résultat de la vérification bulk des affectations
 */
export interface BulkAffectationCheckResult {
  conflits: BulkAffectationConflict[];
  membres_avec_conflits: string[];
  membres_sans_conflits: string[];
}

/**
 * Résultat de la vérification bulk de disponibilité
 */
export interface BulkDisponibiliteResult {
  disponibilites: MembreDisponibilite[];
  membres_disponibles: string[];
  membres_indisponibles: string[];
}
